import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { DownloadSimple } from '@phosphor-icons/react';
import * as XLSX from 'xlsx';
import styles from './BOQExport.module.css';

// Map cost types to their units
const getUnitFromCostType = costType => {
  if (!costType) return '-';
  const unitMap = {
    'מחיר קבוע': 'יח׳',
    'מחיר למטר': 'מ׳',
    'מחיר למטר רבוע': 'מ״ר',
    'מחיר ליחידה': 'יח׳',
    'מחיר חודשי': 'חודש',
  };
  return unitMap[costType.name] || '-';
};

const BOQExport = ({ projectItemRows = [], currentProject, costTypes = [], bbChapters = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const portalRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  // Build chapter lookup
  const chapterById = {};
  bbChapters?.forEach(ch => {
    chapterById[ch.id] = ch;
  });

  // Close dropdown when clicking outside (check both the container and the portaled dropdown)
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!portalRef.current || !portalRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Walk up parent_id chain to find the top-level (depth=0) ancestor chapter
  const getTopLevelChapter = chapterId => {
    if (!chapterId) return null;
    let current = chapterById[chapterId];
    if (!current) return null;
    while (current.parent_id && chapterById[current.parent_id]) {
      current = chapterById[current.parent_id];
    }
    return current;
  };

  // Merge items with same master_item_id + selected_option (mirrors BOQTable logic)
  const mergeItems = items => {
    const mergeMap = new Map();
    items.forEach(item => {
      if (!item.master_item_id) {
        mergeMap.set(item.id, { ...item });
        return;
      }
      const optionKey = JSON.stringify(item.selected_option || {});
      const mergeKey = `${item.master_item_id}__${optionKey}`;
      if (mergeMap.has(mergeKey)) {
        const existing = mergeMap.get(mergeKey);
        existing.quantity = (Number(existing.quantity) || 1) + (Number(item.quantity) || 1);
        existing.estimated_cost =
          (Number(existing.estimated_cost) || 0) + (Number(item.estimated_cost) || 0);
      } else {
        mergeMap.set(mergeKey, { ...item });
      }
    });
    return Array.from(mergeMap.values());
  };

  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      // Merge duplicates
      const merged = mergeItems(projectItemRows);

      // Group by top-level BB chapter (same logic as BOQTable)
      const NO_CHAPTER = '__no_chapter__';
      const groupMap = new Map();

      merged.forEach(item => {
        const itemChapterId = item.master_items?.bb_chapter_id || item.bb_chapter_id || NO_CHAPTER;
        const topChapter = getTopLevelChapter(itemChapterId);
        const groupKey = topChapter?.id || NO_CHAPTER;

        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, {
            code: topChapter?.code || '',
            name: topChapter?.name_he || 'ללא פרק',
            items: [],
          });
        }
        groupMap.get(groupKey).items.push(item);
      });

      // Sort groups by code, "no chapter" last
      const sortedGroups = Array.from(groupMap.entries()).sort(([aKey, a], [bKey, b]) => {
        if (aKey === NO_CHAPTER) return 1;
        if (bKey === NO_CHAPTER) return -1;
        return a.code.localeCompare(b.code, undefined, { numeric: true });
      });

      // Build rows: group header + sorted items within each group
      const headers = ['מספר סעיף', 'סעיף', 'פריט', 'תיאור', 'יחידה', 'כמות'];
      const excelData = [];

      sortedGroups.forEach(([, group]) => {
        // Group header row — merged across all columns visually via bold code + name
        excelData.push({
          'מספר סעיף': group.code,
          סעיף: `${group.code} - ${group.name}`,
          פריט: '',
          תיאור: '',
          יחידה: '',
          כמות: '',
        });

        // Sort items within group by sub-chapter code
        group.items.sort((a, b) => {
          const aId = a.master_items?.bb_chapter_id || a.bb_chapter_id;
          const bId = b.master_items?.bb_chapter_id || b.bb_chapter_id;
          const aCode = aId ? chapterById[aId]?.code || '' : '';
          const bCode = bId ? chapterById[bId]?.code || '' : '';
          return aCode.localeCompare(bCode, undefined, { numeric: true });
        });

        // Item rows
        group.items.forEach(item => {
          const itemChapterId = item.master_items?.bb_chapter_id || item.bb_chapter_id;
          const chapter = itemChapterId ? chapterById[itemChapterId] : null;
          const costTypeId = item.master_items?.cost_type_id || item.details?.cost_type_id;
          const costType = costTypeId ? costTypes?.find(ct => ct.id === costTypeId) : null;

          excelData.push({
            'מספר סעיף': chapter?.code || '-',
            סעיף: chapter?.name_he || '-',
            פריט: item.name_override || item.master_items?.item_name || '-',
            תיאור: item.details?.notes || '-',
            יחידה: getUnitFromCostType(costType),
            כמות: item.quantity || 0,
          });
        });
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData, { header: headers });

      // Set RTL direction for the workbook
      if (!wb.Workbook) wb.Workbook = {};
      if (!wb.Workbook.Views) wb.Workbook.Views = [];
      if (!wb.Workbook.Views[0]) wb.Workbook.Views[0] = {};
      wb.Workbook.Views[0].RTL = true;

      // Set column widths
      ws['!cols'] = [
        { wch: 12 }, // מספר סעיף
        { wch: 20 }, // סעיף
        { wch: 25 }, // פריט
        { wch: 35 }, // תיאור
        { wch: 10 }, // יחידה
        { wch: 10 }, // כמות
      ];

      // Add worksheet properties for RTL
      ws['!dir'] = 'rtl';
      ws['!rtl'] = true;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'כתב כמויות');

      // Generate filename with project name and date
      const date = new Date().toISOString().split('T')[0];
      const filename = `BOQ_${currentProject?.name || 'Project'}_${date}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Failed to export Excel:', error);
    } finally {
      setIsExporting(false);
      setIsOpen(false);
    }
  };

  return (
    <div className={styles.exportContainer} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left });
          }
          setIsOpen(!isOpen);
        }}
        disabled={isExporting}
        className={styles.exportButton}
      >
        <DownloadSimple className={styles.icon} />
        {isExporting ? 'מייצא...' : 'ייצא מסמך'}
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={portalRef}
            className={styles.dropdown}
            style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left }}
          >
            <button onClick={exportToExcel} disabled={isExporting} className={styles.dropdownItem}>
              ייצוא לקובץ Excel
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default BOQExport;
